import FilterMenu from "../../../../components/filters/FilterMenu";
import SearchAndOrFilter from "../../../../components/filters/SearchAndOrFilter";
import TimeFilter from "../../../../components/filters/TimeFilter";
import { getIsAdminList } from "../../../../utils/search";
import IntegrationRowFilter from "../../../posture/steps/components/IntegrationRowFilter";
import { IntegrationsMapping } from "../../integrations/IntegrationsMapping";
import { pastTimeOptions } from "./EntityTypeRegularFilters";
import { ALL_OPTION_NAME, filterName, terminationTimeValues } from "./utils";

const getDepartmentOptions = (fetchedEntities) => {
  const departments = new Set(
    fetchedEntities
      .map(
        (entity) =>
          entity.hibob_data?.department ||
          entity?.bamboohr_data?.department ||
          entity?.kandji_data?.department
      )
      .filter(Boolean)
  );
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All departments",
      displayName: "All",
    },
    ...Array.from(departments).map((department) => ({
      name: department,
      label: department,
      displayName: department,
    })),
  ];
};

const getPrivilegedInOptions = (fetchedEntities) => {
  const tools = new Set(
    fetchedEntities.flatMap((entity) => getIsAdminList(entity)).filter(Boolean)
  );
  return [
    {
      name: ALL_OPTION_NAME,
      label: "All platforms",
      displayName: "All",
    },
    ...Array.from(tools).map((tool) => ({
      name: tool,
      label: <IntegrationRowFilter name={tool} />,
      displayName: IntegrationsMapping[tool].displayName,
    })),
  ];
};

const getOktaAdminsOptions = (fetchedEntities) => {
  const roles = new Set(
    fetchedEntities
      .flatMap((entity) => entity.admin_roles?.okta)
      .filter(Boolean)
  );

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All admin roles",
      displayName: "All",
    },
    ...Array.from(roles).map((role) => ({
      name: role,
      label: role,
      displayName: role,
    })),
  ];
};

const getOktaGroupsOptions = (fetchedEntities) => {
  const groups = new Set(
    fetchedEntities.flatMap((entity) =>
      entity?.okta_data?.groups && Array.isArray(entity.okta_data.groups)
        ? entity.okta_data.groups.map((group) => group?.name).filter(Boolean)
        : []
    )
  );

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All groups",
      displayName: "All",
    },
    ...Array.from(groups).map((group) => ({
      name: group,
      label: group,
      displayName: group,
    })),
  ];
};

const getOktaStatusOptions = (fetchedEntities) => {
  const statuses = new Set(
    fetchedEntities
      .flatMap((entity) => entity.okta_data?.status)
      .filter(Boolean)
  );

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All statuses",
      displayName: "All",
    },
    ...Array.from(statuses).map((status) => ({
      name: status,
      label: status,
      displayName: status,
    })),
  ];
};

const getPoliciesOptions = (fetchedEntities) => {
  const policiesWithSource = new Map();

  fetchedEntities.forEach((entity) => {
    if (entity?.crowdstrike_data?.policies) {
      entity.crowdstrike_data.policies.forEach((policy) => {
        if (policy.policy_name) {
          const uniqueKey = `${policy.policy_name}__crowdstrike`;
          policiesWithSource.set(uniqueKey, {
            policyName: policy.policy_name,
            source: "crowdstrike",
          });
        }
      });
    }
    if (entity?.okta_data?.policies) {
      entity.okta_data.policies.forEach((policy) => {
        if (policy.policy_name) {
          const uniqueKey = `${policy.policy_name}__okta`;
          policiesWithSource.set(uniqueKey, {
            policyName: policy.policy_name,
            source: "okta",
          });
        }
      });
    }
    if (entity?.aws_data?.policies) {
      entity.aws_data.policies.forEach((policy) => {
        if (policy.policy_name) {
          const uniqueKey = `${policy.policy_name}__aws`;
          policiesWithSource.set(uniqueKey, {
            policyName: policy.policy_name,
            source: "aws",
          });
        }
      });
    }
  });

  return [
    {
      name: ALL_OPTION_NAME,
      label: "All policies",
      displayName: "All",
    },
    ...Array.from(policiesWithSource.entries())
      .map(([uniqueKey, policyData]) => ({
        name: uniqueKey,
        label: (
          <IntegrationRowFilter
            name={policyData.source}
            label={policyData.policyName}
          />
        ),
        displayName: policyData.policyName,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  ];
};

const terminationTimeOptions = Object.entries(terminationTimeValues).map(
  ([name, info]) => ({
    name: name,
    label: info.label,
    displayName: info.label,
  })
);

export const getUserFilters = (params) => [
  <FilterMenu
    filterName={"Department"}
    fieldName={filterName.DEPARTMENT}
    options={getDepartmentOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
  />,
  <SearchAndOrFilter
    filterName={"Privileged In"}
    fieldName={filterName.PRIVILEGED_IN}
    options={getPrivilegedInOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.PRIVILEGED_IN]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <SearchAndOrFilter
    filterName={"Okta Admins"}
    fieldName={filterName.OKTA_ADMINS}
    options={getOktaAdminsOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.OKTA_ADMINS]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <SearchAndOrFilter
    filterName={"Okta Groups"}
    fieldName={filterName.OKTA_GROUPS}
    options={getOktaGroupsOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.OKTA_GROUPS]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <SearchAndOrFilter
    filterName={"Okta Status"}
    fieldName={filterName.OKTA_STATUS}
    options={getOktaStatusOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.OKTA_STATUS]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <SearchAndOrFilter
    filterName={"Policies"}
    fieldName={filterName.POLICIES}
    options={getPoliciesOptions(params.fetchedEntities)}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.POLICIES]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <TimeFilter
    filterName={"Last Active"}
    fieldName={filterName.LAST_ACTIVE}
    options={pastTimeOptions}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    filterLogic={params.filterAndOrLogics[filterName.LAST_ACTIVE]}
    setFilterLogic={params.setFilterAndOrLogics}
  />,
  <FilterMenu
    filterName={"Termination in"}
    fieldName={filterName.TERMINATION_DATE}
    options={terminationTimeOptions}
    appliedFilters={params.appliedFilters}
    setAppliedFilters={params.setAppliedFilters}
    singleSelect
  />,
];
